import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { deleteSavedRequest, getRequestDisplayTitle, listSavedRequests, migrateLegacyLocalStorage, type SavedGroupSetupRequest } from '../data/savedRequests';
import { useAuth } from '../context/AuthContext';
import { LogOutIcon, PlusIcon, PencilIcon, Trash2Icon } from 'lucide-react';
function formatDate(iso: string): string {
  try {
    return new Intl.DateTimeFormat(undefined, {
      dateStyle: 'medium',
      timeStyle: 'short'
    }).format(new Date(iso));
  } catch {
    return iso;
  }
}
export function SavedRequestsListPage() {
  const {
    user,
    logout
  } = useAuth();
  const navigate = useNavigate();
  const [requests, setRequests] = useState<SavedGroupSetupRequest[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [deletingId, setDeletingId] = useState<number | null>(null);
  const refresh = async () => {
    setIsLoading(true);
    setLoadError(null);
    try {
      const items = await listSavedRequests();
      setRequests(items);
    } catch (err: unknown) {
      setLoadError(err instanceof Error ? err.message : 'Failed to load saved requests');
    } finally {
      setIsLoading(false);
    }
  };
  useEffect(() => {
    (async () => {
      try {
        await migrateLegacyLocalStorage();
      } catch {
        // ignore migration errors
      }
      await refresh();
    })();
  }, []);
  const handleDelete = async (request: SavedGroupSetupRequest) => {
    const title = getRequestDisplayTitle(request);
    if (!window.confirm(`Delete "${title}"? This cannot be undone.`)) {
      return;
    }
    setDeletingId(request.id);
    try {
      await deleteSavedRequest(request.id);
      await refresh();
    } catch (err: unknown) {
      alert(err instanceof Error ? err.message : 'Failed to delete request');
    } finally {
      setDeletingId(null);
    }
  };
  return <div className="min-h-screen bg-background py-8 px-4 sm:px-6 lg:px-8" data-id="element-381">
      <div className="max-w-4xl mx-auto" data-id="element-382">
        <div className="mb-8 flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4" data-id="element-383">
          <div className="text-center sm:text-left" data-id="element-384">
            <img src="https://mirrorful-production.s3.us-west-1.amazonaws.com/patterns/assets/pzhmlekvyppnluh27wy1t9/f3160bd3-c8c6-4ee6-97ae-1dc6fc5bb54a/logo.png" alt="Lawvo" className="h-10 mx-auto sm:mx-0 mb-3" data-id="element-385" />
            <h1 className="text-2xl sm:text-3xl font-bold text-foreground" data-id="element-386">
              Group Setup Requests
            </h1>
            <p className="text-sm text-muted-foreground mt-1" data-id="element-387">
              Saved in lawvo-api for onboarding alignment and permission cross-checks.
            </p>
            {user && <p className="text-sm text-muted-foreground mt-1" data-id="element-388">Signed in as {user.email}</p>}
          </div>
          <div className="flex items-center justify-center sm:justify-end gap-2" data-id="element-389">
            <button type="button" onClick={logout} className="flex items-center gap-1 px-3 py-2 text-sm text-muted-foreground hover:text-foreground border border-input rounded-md" data-id="element-390">
              <LogOutIcon className="w-4 h-4" data-id="element-391" />
              Sign Out
            </button>
            <button type="button" onClick={() => navigate('/setup/new')} className="flex items-center gap-1.5 px-4 py-2 bg-violet-blue text-white rounded-md text-sm font-medium hover:bg-violet-blue/90" data-id="element-392">
              <PlusIcon className="w-4 h-4" data-id="element-393" />
              New Request
            </button>
          </div>
        </div>

        {loadError && <div className="mb-4 rounded-md border border-destructive/30 bg-destructive/10 px-4 py-3 text-sm text-destructive" data-id="element-394">
            {loadError}{' '}
            <button type="button" className="underline" onClick={() => refresh()} data-id="element-395">
              Retry
            </button>
          </div>}

        {isLoading ? <div className="flex justify-center py-16" data-id="element-396">
            <div className="w-8 h-8 border-2 border-violet-blue/30 border-t-violet-blue rounded-full animate-spin" data-id="element-397" />
          </div> : requests.length === 0 ? <div className="bg-card border border-border rounded-lg shadow-sm p-12 text-center" data-id="element-398">
            <p className="text-muted-foreground mb-6" data-id="element-399">
              No saved group setup requests yet. Create one to capture onboarding requirements
              for a new group.
            </p>
            <button type="button" onClick={() => navigate('/setup/new')} className="inline-flex items-center gap-1.5 px-4 py-2 bg-violet-blue text-white rounded-md text-sm font-medium hover:bg-violet-blue/90" data-id="element-400">
              <PlusIcon className="w-4 h-4" data-id="element-401" />
              Create First Request
            </button>
          </div> : <div className="bg-card border border-border rounded-lg shadow-sm overflow-hidden" data-id="element-402">
            <div className="overflow-x-auto" data-id="element-403">
              <table className="w-full text-sm" data-id="element-404">
                <thead data-id="element-405">
                  <tr className="border-b border-border bg-muted/30" data-id="element-406">
                    <th className="text-left font-semibold text-foreground px-4 py-3" data-id="element-407">Group</th>
                    <th className="text-left font-semibold text-foreground px-4 py-3 hidden sm:table-cell" data-id="element-408">
                      Submitter
                    </th>
                    <th className="text-left font-semibold text-foreground px-4 py-3 hidden md:table-cell" data-id="element-409">
                      Last Updated
                    </th>
                    <th className="text-left font-semibold text-foreground px-4 py-3" data-id="element-410">Status</th>
                    <th className="text-right font-semibold text-foreground px-4 py-3" data-id="element-411">Actions</th>
                  </tr>
                </thead>
                <tbody data-id="element-412">
                  {requests.map(request => <tr key={request.id} className="border-b border-border last:border-0 hover:bg-muted/20" data-id="element-413">
                      <td className="px-4 py-3" data-id="element-414">
                        <Link to={`/setup/${request.id}`} className="font-medium text-foreground hover:text-violet-blue hover:underline" data-id="element-415">
                          {getRequestDisplayTitle(request)}
                        </Link>
                        <p className="text-xs text-muted-foreground mt-0.5 sm:hidden" data-id="element-416">
                          {request.formData.submitterName || 'No submitter'}
                        </p>
                      </td>
                      <td className="px-4 py-3 text-muted-foreground hidden sm:table-cell" data-id="element-417">
                        <div data-id="element-418">{request.formData.submitterName || '—'}</div>
                        <div className="text-xs" data-id="element-419">{request.formData.submitterEmail}</div>
                      </td>
                      <td className="px-4 py-3 text-muted-foreground hidden md:table-cell" data-id="element-420">
                        {formatDate(request.updatedAt)}
                      </td>
                      <td className="px-4 py-3" data-id="element-421">
                        <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium ${request.status === 'submitted' ? 'bg-emerald-100 text-emerald-800' : 'bg-amber-100 text-amber-900'}`} data-id="element-422">
                          {request.status === 'submitted' ? 'Submitted' : 'Draft'}
                        </span>
                      </td>
                      <td className="px-4 py-3" data-id="element-423">
                        <div className="flex items-center justify-end gap-2" data-id="element-424">
                          <Link to={`/setup/${request.id}`} className="inline-flex items-center gap-1 px-2.5 py-1.5 text-xs font-medium border border-input rounded-md hover:bg-muted" data-id="element-425">
                            <PencilIcon className="w-3.5 h-3.5" data-id="element-426" />
                            Edit
                          </Link>
                          <button type="button" disabled={deletingId === request.id} onClick={() => handleDelete(request)} className="inline-flex items-center gap-1 px-2.5 py-1.5 text-xs font-medium text-destructive border border-destructive/30 rounded-md hover:bg-destructive/10 disabled:opacity-60" data-id="element-427">
                            <Trash2Icon className="w-3.5 h-3.5" data-id="element-428" />
                            Delete
                          </button>
                        </div>
                      </td>
                    </tr>)}
                </tbody>
              </table>
            </div>
          </div>}
      </div>
    </div>;
}